import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Award,
    BadgeCheck,
    Globe2,
    FileSearch,
    FileText,
    PenTool,
    ShieldCheck,
    GraduationCap,
    Building2,
    Banknote,
    HeartPulse,
    ShoppingBag,
    Landmark,
    BookOpen,
    Cpu,
    Star,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { Card } from "../components/ui/card";

/* --------- HERO --------- */
const Hero = () => (
    <section className="relative overflow-hidden" aria-labelledby="hero-heading">
        <div className="absolute inset-0 -z-10" aria-hidden="true">
            <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-[#F1F7F8]" />
            <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-[#127A75]/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#0A2540]/10 blur-3xl" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7 an-fade-up">
                    <span className="inline-flex items-center gap-2 rounded-full border border-brand-border bg-white px-3 py-1 text-xs font-semibold text-brand-teal">
                        <BadgeCheck size={14} aria-hidden="true" />
                        WCAG 2.2 · ADA · EAA · Section 508
                    </span>
                    <h1
                        id="hero-heading"
                        className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-text tracking-tight"
                    >
                        Accessibility, Engineered for{" "}
                        <span className="text-brand-teal">Global Compliance.</span>
                    </h1>
                    <p className="mt-6 text-lg text-brand-body max-w-2xl">
                        WCAG, ADA, EAA, Section 508, AODA, BITV, and global accessibility audits,
                        document remediation, and design reviews — by a team with 12+ years of
                        specialized experience. Trusted by businesses across the US, Europe,
                        Canada, and Asia.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <Link
                            to="/free-audit"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors duration-200 touch-target"
                            data-testid="hero-cta-primary"
                        >
                            Get a Free Mini Audit
                            <ArrowRight size={18} aria-hidden="true" />
                        </Link>
                        <Link
                            to="/services"
                            className="inline-flex items-center justify-center rounded-md border-2 border-brand-navy text-brand-navy font-semibold px-6 py-3 hover:bg-brand-navy hover:text-white transition-colors duration-200 touch-target"
                            data-testid="hero-cta-secondary"
                        >
                            View Services
                        </Link>
                    </div>
                </div>

                {/* Abstract geometric illustration */}
                <div className="lg:col-span-5 an-fade-up">
                    <div className="relative aspect-square max-w-md mx-auto">
                        <svg viewBox="0 0 400 400" className="w-full h-full" role="img"
                             aria-label="Abstract geometric illustration representing accessibility and compliance">
                            <defs>
                                <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor="#0A2540" />
                                    <stop offset="100%" stopColor="#127A75" />
                                </linearGradient>
                            </defs>
                            <circle cx="200" cy="200" r="160" fill="url(#g1)" opacity="0.08" />
                            <circle cx="200" cy="200" r="120" fill="none" stroke="#127A75" strokeWidth="1.5" opacity="0.4" />
                            <circle cx="200" cy="200" r="80" fill="none" stroke="#0A2540" strokeWidth="1.5" opacity="0.5" />
                            <rect x="150" y="150" width="100" height="100" rx="12" fill="#0A2540" />
                            <path d="M170 210 L195 235 L240 175" stroke="#1A8B82" strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="80" cy="100" r="8" fill="#1A8B82" />
                            <circle cx="330" cy="120" r="6" fill="#0A2540" />
                            <circle cx="100" cy="320" r="10" fill="#127A75" />
                            <circle cx="320" cy="310" r="7" fill="#1A8B82" />
                            <line x1="80" y1="100" x2="150" y2="170" stroke="#1A8B82" strokeWidth="1.5" opacity="0.4" />
                            <line x1="330" y1="120" x2="250" y2="170" stroke="#0A2540" strokeWidth="1.5" opacity="0.4" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* --------- TRUST STRIP --------- */
const STATS = [
    { icon: Award, label: "12+ Years Experience", testId: "stat-experience" },
    { icon: ShieldCheck, label: "500+ Audits Delivered", testId: "stat-audits" },
    { icon: Globe2, label: "Global Coverage: US, EU, Canada, Asia", testId: "stat-coverage" },
];

const TrustStrip = () => (
    <section
        aria-label="Key metrics"
        className="bg-brand-soft border-y border-brand-border"
    >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {STATS.map(({ icon: Icon, label, testId }) => (
                    <li key={label} className="flex items-center gap-4 justify-center md:justify-start" data-testid={testId}>
                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-brand-border text-brand-teal">
                            <Icon size={22} aria-hidden="true" />
                        </span>
                        <span className="text-brand-text font-semibold">{label}</span>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* --------- SERVICES --------- */
const SERVICES = [
    {
        icon: FileSearch,
        title: "Web & Mobile Audits",
        desc: "WCAG 2.1/2.2 audits for websites and mobile apps with detailed remediation roadmaps.",
        to: "/services#web-audits",
    },
    {
        icon: FileText,
        title: "Document Remediation",
        desc: "PDF, Word, PowerPoint, and EPUB accessibility using CommonLook, Acrobat Pro, and PAC 2024.",
        to: "/services#pdf-remediation",
    },
    {
        icon: PenTool,
        title: "Design Audits",
        desc: "Catch accessibility issues at the design stage in Figma, Sketch, and Adobe XD — before development.",
        to: "/services#design-audits",
    },
    {
        icon: Globe2,
        title: "Global Compliance",
        desc: "ADA, EAA, Section 508, AODA, BITV 2.0, RGAA, and other international standards.",
        to: "/services#compliance",
    },
];

const Services = () => (
    <section aria-labelledby="services-heading" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">What we do</p>
                <h2 id="services-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                    Services built around real compliance outcomes.
                </h2>
                <p className="mt-4 text-base text-brand-body">
                    Audits and remediation programs designed to move your platform from
                    risk-prone to provably compliant — with documentation your legal team can cite.
                </p>
            </div>

            <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {SERVICES.map(({ icon: Icon, title, desc, to }) => (
                    <li key={title}>
                        <Card
                            className="h-full p-6 border-brand-border hover:shadow-cardHover transition-shadow duration-200 group"
                            data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                            <div className="w-11 h-11 rounded-md bg-brand-soft text-brand-teal inline-flex items-center justify-center group-hover:bg-brand-teal group-hover:text-white transition-colors">
                                <Icon size={22} aria-hidden="true" />
                            </div>
                            <h3 className="mt-5 text-lg font-bold text-brand-text">{title}</h3>
                            <p className="mt-2 text-sm text-brand-body leading-relaxed">{desc}</p>
                            <Link
                                to={to}
                                className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-teal hover:text-brand-tealBright"
                                aria-label={`Learn more about ${title}`}
                            >
                                Learn more
                                <ArrowRight size={14} aria-hidden="true" />
                            </Link>
                        </Card>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* --------- WHY ACCESSNOVA --------- */
const WHYS = [
    { title: "Specialized Expertise", desc: "Only accessibility, nothing else. Every team member is a certified specialist." },
    { title: "Global Standards", desc: "WCAG, ADA, EAA, Section 508, AODA across all major markets." },
    { title: "Real Reports", desc: "Actionable findings with code snippets and screenshots — not jargon." },
    { title: "Ongoing Partnership", desc: "Audit, fix, monitor, repeat. We stay with you beyond the report." },
];

const Why = () => (
    <section aria-labelledby="why-heading" className="bg-brand-soft py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Why AccessNova</p>
                <h2 id="why-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                    Specialists, not generalists.
                </h2>
            </div>
            <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                {WHYS.map((w, i) => (
                    <li key={w.title} data-testid={`why-card-${i}`}>
                        <Card className="h-full p-7 bg-white border-brand-border hover:shadow-cardHover transition-shadow flex gap-5">
                            <span className="flex-none text-3xl font-bold text-brand-teal" aria-hidden="true">
                                0{i + 1}
                            </span>
                            <div>
                                <h3 className="text-lg font-bold text-brand-text">{w.title}</h3>
                                <p className="mt-2 text-sm text-brand-body leading-relaxed">{w.desc}</p>
                            </div>
                        </Card>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* --------- HOW WE WORK --------- */
const STEPS = [
    { n: "1", title: "Discovery", desc: "Understand your platform, users, and the compliance frameworks that apply to your markets." },
    { n: "2", title: "Audit & Report", desc: "Manual + automated testing, prioritized findings with severity, effort, and remediation guidance." },
    { n: "3", title: "Remediate & Monitor", desc: "Fix support, retesting, and ongoing compliance monitoring as your product evolves." },
];

const HowWeWork = () => (
    <section aria-labelledby="how-heading" className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Our process</p>
                <h2 id="how-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                    How we work — three clear steps.
                </h2>
            </div>
            <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                {STEPS.map((s) => (
                    <li key={s.n} data-testid={`step-${s.n}`}>
                        <Card className="h-full p-7 border-brand-border bg-white hover:shadow-cardHover transition-shadow relative">
                            <span className="absolute -top-5 left-7 inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-navy text-white font-bold text-xl shadow-card">
                                {s.n}
                            </span>
                            <h3 className="mt-6 text-xl font-bold text-brand-text">{s.title}</h3>
                            <p className="mt-3 text-sm text-brand-body leading-relaxed">{s.desc}</p>
                        </Card>
                    </li>
                ))}
            </ol>
        </div>
    </section>
);

/* --------- INDUSTRIES --------- */
const INDUSTRIES = [
    { icon: ShoppingBag, label: "E-commerce" },
    { icon: HeartPulse, label: "Healthcare" },
    { icon: Banknote, label: "Banking" },
    { icon: GraduationCap, label: "Education" },
    { icon: Landmark, label: "Government" },
    { icon: Cpu, label: "SaaS" },
    { icon: BookOpen, label: "Publishing" },
];

const Industries = () => (
    <section aria-labelledby="industries-heading" className="bg-brand-soft py-16 border-y border-brand-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 id="industries-heading" className="text-center text-sm font-semibold uppercase tracking-wider text-brand-muted">
                Industries we serve
            </h2>
            <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
                {INDUSTRIES.map(({ icon: Icon, label }) => (
                    <li key={label} className="flex items-center gap-2 text-brand-text" data-testid={`industry-${label.toLowerCase()}`}>
                        <Icon size={20} className="text-brand-teal" aria-hidden="true" />
                        <span className="font-semibold text-sm">{label}</span>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* --------- TESTIMONIALS --------- */
const TESTIMONIALS = [
    {
        name: "Sarah Mitchell",
        role: "VP of Engineering",
        company: "Halcyon Health",
        rating: 5,
        quote: "AccessNova's audit was the most thorough we've ever received. The remediation roadmap was actionable from day one — our team shipped fixes in two sprints, not two quarters.",
        initials: "SM",
    },
    {
        name: "Daniel Okafor",
        role: "Head of Product Design",
        company: "Lumen Commerce",
        rating: 5,
        quote: "Their design audit caught issues in our Figma library that would have cost us months in development. They pay for themselves immediately.",
        initials: "DO",
    },
    {
        name: "Marie Lefèvre",
        role: "Compliance Director",
        company: "Aurora Banking Group",
        rating: 5,
        quote: "EAA was a hard deadline. AccessNova delivered a defensible audit, helped our vendors close gaps, and produced a VPAT we could publish with confidence.",
        initials: "ML",
    },
    {
        name: "Rajiv Sharma",
        role: "CTO",
        company: "Atlas Learning",
        rating: 5,
        quote: "Clear findings, no jargon, and a partnership that didn't end at the report. They retested every release for six months.",
        initials: "RS",
    },
];

const Testimonials = () => {
    const [idx, setIdx] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    const timer = React.useRef(null);
    const total = TESTIMONIALS.length;

    React.useEffect(() => {
        if (paused) return;
        timer.current = setInterval(() => setIdx((i) => (i + 1) % total), 6000);
        return () => clearInterval(timer.current);
    }, [paused, total]);

    const prev = () => setIdx((i) => (i - 1 + total) % total);
    const next = () => setIdx((i) => (i + 1) % total);

    // Show 3 cards on desktop window starting at idx
    const visible = [0, 1, 2].map((o) => TESTIMONIALS[(idx + o) % total]);

    return (
        <section
            aria-labelledby="testimonials-heading"
            className="py-20 lg:py-24"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div className="max-w-2xl">
                        <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Client voices</p>
                        <h2 id="testimonials-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                            Trusted by teams who can't afford to ship inaccessible.
                        </h2>
                    </div>
                    <div className="flex gap-2" role="group" aria-label="Testimonial controls">
                        <button
                            onClick={prev}
                            aria-label="Previous testimonial"
                            className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-brand-border text-brand-navy hover:bg-brand-soft transition-colors touch-target"
                            data-testid="testimonial-prev"
                        >
                            <ChevronLeft size={20} aria-hidden="true" />
                        </button>
                        <button
                            onClick={next}
                            aria-label="Next testimonial"
                            className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-brand-border text-brand-navy hover:bg-brand-soft transition-colors touch-target"
                            data-testid="testimonial-next"
                        >
                            <ChevronRight size={20} aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <div
                    className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    aria-live="polite"
                    aria-atomic="false"
                >
                    {visible.map((t, i) => (
                        <Card
                            key={`${t.name}-${i}`}
                            className="p-7 bg-white border-brand-border hover:shadow-cardHover transition-shadow"
                            data-testid={`testimonial-card-${i}`}
                        >
                            <div className="flex items-center gap-1 text-brand-warning" aria-label={`${t.rating} out of 5 stars`}>
                                {Array.from({ length: t.rating }).map((_, j) => (
                                    <Star key={j} size={16} fill="currentColor" aria-hidden="true" />
                                ))}
                            </div>
                            <blockquote className="mt-4 text-base text-brand-text leading-relaxed">
                                "{t.quote}"
                            </blockquote>
                            <figcaption className="mt-6 flex items-center gap-3 not-italic">
                                <span
                                    aria-hidden="true"
                                    className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-brand-navy text-white font-bold text-sm"
                                >
                                    {t.initials}
                                </span>
                                <span>
                                    <span className="block text-sm font-semibold text-brand-text">{t.name}</span>
                                    <span className="block text-xs text-brand-muted">{t.role}, {t.company}</span>
                                </span>
                            </figcaption>
                        </Card>
                    ))}
                </div>

                <p className="sr-only" aria-live="polite">
                    Showing testimonial {idx + 1} of {total}.
                </p>
            </div>
        </section>
    );
};

/* --------- FREE AUDIT BANNER --------- */
const AuditBanner = () => (
    <section aria-labelledby="audit-cta-heading" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0A2540] via-[#0F3552] to-[#127A75]" aria-hidden="true" />
        <div className="absolute inset-0 -z-10 opacity-20" aria-hidden="true">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                <defs>
                    <pattern id="dots" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                        <circle cx="1" cy="1" r="1" fill="#FFFFFF" opacity="0.4" />
                    </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#dots)" />
            </svg>
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <div className="max-w-3xl">
                <Building2 className="text-white/80" size={36} aria-hidden="true" />
                <h2 id="audit-cta-heading" className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                    See where your site stands. Free.
                </h2>
                <p className="mt-4 text-lg text-white/85 max-w-2xl">
                    Get a 1-page accessibility snapshot of your website within 48 hours.
                    No obligation.
                </p>
                <Link
                    to="/free-audit"
                    className="mt-8 inline-flex items-center gap-2 rounded-md bg-white text-brand-navy hover:bg-[#E6FFFB] font-semibold px-7 py-4 transition-colors duration-200 touch-target"
                    data-testid="banner-cta-free-audit"
                >
                    Request Free Audit
                    <ArrowRight size={18} aria-hidden="true" />
                </Link>
            </div>
        </div>
    </section>
);

/* --------- HOME PAGE --------- */
export default function Home() {
    React.useEffect(() => {
        document.title = "AccessNova — Accessibility, Engineered for Global Compliance";
    }, []);

    return (
        <>
            <Hero />
            <TrustStrip />
            <Services />
            <Why />
            <HowWeWork />
            <Industries />
            {/* <Testimonials /> */}
            <AuditBanner />
        </>
    );
}
